import { fmtCompactNumber, isAbbreviated } from './formatCompactNumber';

/**
 * Massa sempre em quilos, para o Dashboard: número cheio com separador de
 * milhar até 1 milhão ("437.512 kg"), "mi" a partir daí ("11,4 mi kg"). Nunca
 * toneladas, nunca "K".
 *
 * **Por que não "K".** "4,3K kg" gerava dúvida de leitura no cliente, e o
 * truncamento em uma casa comia até 99 kg por valor: somando o tooltip por
 * material, o total não fechava com a planilha (140 kg de diferença num caso
 * real). Abaixo de 1 milhão o número cheio cabe no card e no tooltip, então
 * abreviar só tirava precisão. Acima disso a abreviação segue
 * `fmtCompactNumber` (mesma dos outros KPIs), e quem exibe o valor abreviado
 * deve oferecer o exato — ver `CompactValue`.
 *
 * Trunca, nunca arredonda para cima (GR-548): o cliente confere contra a fatura.
 */

/** A partir daqui o valor é abreviado com "mi". */
export const MILLION_IN_KG = 1_000_000;

export interface FormattedMass {
  value: string;
  unit: 'kg';
}

function truncateToOneDecimal(value: number): number {
  return Math.trunc(value * 10) / 10;
}

export function fmtMass(kg: number): FormattedMass {
  if (!Number.isFinite(kg) || kg <= 0) return { value: '0', unit: 'kg' };

  if (kg < 1) {
    // Abaixo de 1 kg mantém até 3 casas, como `fmtWeight` — pesagens pequenas
    // (amostras, resíduos perigosos) perderiam a informação inteira sem isso.
    const truncated = Math.trunc(kg * 1000) / 1000;
    return {
      value: truncated.toLocaleString('pt-BR', {
        minimumFractionDigits: 0,
        maximumFractionDigits: 3,
      }),
      unit: 'kg',
    };
  }

  if (isAbbreviated(kg)) return { value: fmtCompactNumber(kg), unit: 'kg' };

  return { value: Math.trunc(kg).toLocaleString('pt-BR'), unit: 'kg' };
}

/** String pronta, ex.: `"437.512 kg"` — para tooltip e texto inline. */
export function fmtMassStr(kg: number): string {
  const { value, unit } = fmtMass(kg);
  return `${value} ${unit}`;
}

/**
 * Média, sempre com uma casa decimal abaixo de 1 milhão de kg — `fmtMass`
 * trunca para inteiro, o que serve a totais mas destrói a informação de uma
 * média: "10 kg por coleta" esconde a diferença entre 10,0 e 10,9.
 */
export function fmtMassAverage(kg: number): string {
  if (!Number.isFinite(kg) || kg <= 0) return '0,0 kg';

  if (kg < MILLION_IN_KG) {
    return `${truncateToOneDecimal(kg).toLocaleString('pt-BR', {
      minimumFractionDigits: 1,
      maximumFractionDigits: 1,
    })} kg`;
  }

  return fmtMassStr(kg);
}
